// TaskModal.tsx
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Upload, message } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { TaskModalProps, ImageUploadResponse } from './types';
import type { UploadFile, UploadProps } from 'antd';
import { getBaseUrl } from '../../../../../api/axiosInstance';

const { TextArea } = Input;

const TaskModal: React.FC<TaskModalProps> = ({ 
    visible, 
    editingTask, 
    onSave, 
    onCancel 
}) => {
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const [currentImageId, setCurrentImageId] = useState<string | undefined>(undefined);

    // Функция для получения URL изображения по ID
    const getImageUrl = (imageId: string) => {
        return `${getBaseUrl()}/server/olympiad/files/img?id=${imageId}`;
    };

    useEffect(() => {
        if (visible) {
            if (editingTask) {
                form.setFieldsValue({
                    question: editingTask.question,
                    answer: editingTask.answer,
                });
                
                // Если есть прикрепленное изображение, добавляем его в список
                if (editingTask.imageId) {
                    setCurrentImageId(editingTask.imageId);
                    setFileList([{
                        uid: '-1',
                        name: editingTask.fileName || 'Прикрепленное изображение',
                        status: 'done',
                        url: getImageUrl(editingTask.imageId),
                    }]);
                } else {
                    setFileList([]);
                    setCurrentImageId(undefined);
                }
            } else {
                // Сброс формы при добавлении нового задания
                form.resetFields();
                setFileList([]);
                setCurrentImageId(undefined);
            }
        }
    }, [visible, editingTask, form]); // Добавить visible в зависимости

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            
            // Подготавливаем данные для сохранения
            const saveData = {
                ...values,
                imageId: currentImageId,
                fileName: fileList[0]?.name || editingTask?.fileName
            };
            
            onSave(saveData);
        } catch (error) {
            // Валидация не прошла
        }
    };

    const handleFileChange: UploadProps['onChange'] = ({ fileList: newFileList, file }) => {
        // Ограничиваем загрузку одним файлом
        const updatedFileList = newFileList.slice(-1);
        setFileList(updatedFileList);

        // Обрабатываем статусы загрузки
        if (file.status === 'uploading') {
            setUploading(true);
        } else if (file.status === 'done') {
            setUploading(false);
            // Сервер возвращает идентификатор изображения
            if (file.response && file.response.imageId) {
                setCurrentImageId(file.response.imageId);
                message.success(`${file.name} успешно загружен`);
            }
        } else if (file.status === 'error') {
            setUploading(false);
            message.error(`${file.name} ошибка загрузки`);
        } else if (file.status === 'removed') {
            setCurrentImageId(undefined);
        }
    };

    const handleRemoveFile = () => {
        setFileList([]);
        setCurrentImageId(undefined);
    };

    const beforeUpload = (file: File) => {
        // Проверяем тип файла - только изображения
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('Можно загружать только изображения (JPG, PNG, GIF, etc.)!');
            return false;
        }

        // Проверяем размер файла - не более 5MB
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('Изображение должно быть меньше 5MB!');
            return false;
        }

        return true;
    };

    // Кастомный request для обработки ответа сервера
    const customRequest = async (options: any) => {
        const { file, onSuccess, onError, onProgress } = options;
        
        const formData = new FormData();
        formData.append('file', file);
        
        // Добавляем идентификатор олимпиады, если редактируем существующую задачу
        if (editingTask && editingTask.id) {
            formData.append('taskId', editingTask.id.toString());
        }
        
        try {
            const xhr = new XMLHttpRequest();
            
            // Отслеживаем прогресс загрузки
            if (onProgress) {
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable) {
                        onProgress({ percent: (event.loaded / event.total) * 100 });
                    }
                };
            }
            
            xhr.onload = () => {
                if (xhr.status === 200) {
                    const response: ImageUploadResponse = JSON.parse(xhr.responseText);
                    onSuccess(response, xhr);
                } else {
                    onError(new Error('Ошибка загрузки'));
                }
            };
            
            xhr.onerror = () => {
                onError(new Error('Ошибка сети'));
            };
            
            xhr.withCredentials = true;
            xhr.open('POST', `${getBaseUrl()}/server/olympiad/files/upload`, true);
            xhr.send(formData);
        } catch (error) {
            onError(error);
        }
    };

    return (
        <Modal
            title={editingTask ? 'Редактировать задание' : 'Добавить задание'}
            open={visible}
            onOk={handleOk}
            onCancel={onCancel}
            width={600}
            className="task-modal"
            footer={[
                <Button key="cancel" onClick={onCancel} size="small">
                    Отмена
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    onClick={handleOk}
                    size="small"
                    loading={uploading}
                >
                    {editingTask ? 'Сохранить' : 'Добавить'}
                </Button>
            ]}
        >
            <Form form={form} layout="vertical" size="small" className="task-form">
                <Form.Item
                    name="question"
                    label="Вопрос"
                    rules={[{ required: true, message: 'Введите вопрос' }]}
                >
                    <TextArea rows={2} placeholder="Текст вопроса" />
                </Form.Item>

                <Form.Item
                    name="answer"
                    label="Ответ"
                    rules={[{ required: true, message: 'Введите ответ' }]}
                >
                    <TextArea rows={2} placeholder="Текст ответа" />
                </Form.Item>

                <Form.Item
                    label="Прикрепленное изображение"
                    help="Можно загрузить одно изображение (JPG, PNG, GIF, макс. 5MB)"
                >
                    <Upload
                        fileList={fileList}
                        onChange={handleFileChange}
                        onRemove={handleRemoveFile}
                        beforeUpload={beforeUpload}
                        customRequest={customRequest}
                        maxCount={1}
                        accept="image/*"
                        listType="picture"
                    >
                        <Button icon={<UploadOutlined />} size="small">
                            Выбрать изображение
                        </Button>
                    </Upload>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default TaskModal;